import { Assignment, Inventory2Outlined } from '@mui/icons-material';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney } from 'lib/functions/utils';
import { PurchaseOrderItem } from 'lib/types/models';
import React from 'react';

const POItemsSection: React.FC<{ items: PurchaseOrderItem[] }> = ({
  items,
}) => {
  const hasItems = Array.isArray(items) && items.length > 0;

  return (
    <Paper
      sx={{
        px: 1,
        py: 1.25,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '0.95rem',
            lineHeight: 1.15,
          }}
        >
          Items de la Orden de Compra
        </Typography>
        <Chip
          label="Detalles"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: '16px', fontSize: '0.6rem' }}
        />
      </Box>

      <Box
        sx={{
          ...(hasItems
            ? { maxHeight: '30vh', overflowY: 'auto' }
            : { overflowY: 'hidden', minHeight: 140 }),
          '&::-webkit-scrollbar': hasItems
            ? { width: '6px' }
            : { display: 'none' },
          '&::-webkit-scrollbar-track': hasItems
            ? { backgroundColor: '#f1f1f1', borderRadius: '3px' }
            : {},
          scrollbarWidth: hasItems ? 'auto' : 'none',
        }}
      >
        {(!items || items.length === 0) && (
          <Box
            sx={{
              py: 3,
              px: 2,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 1,
              width: '100%',
              minHeight: 'inherit',
            }}
          >
            <Inventory2Outlined sx={{ fontSize: 18, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              No hay items en esta orden de compra
            </Typography>
          </Box>
        )}
        <Grid container spacing={1}>
          {items.map((item: PurchaseOrderItem, index: number) => (
            <Grid item xs={12} key={item.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  border: '1px solid #f0f0f0',
                  backgroundColor: '#fafafa',
                }}
              >
                <Grid container spacing={1} alignItems="stretch">
                  <Grid item xs={8} sm={7}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          borderRadius: '50%',
                          p: 0.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Assignment
                          sx={{ fontSize: '0.65rem', color: 'primary.main' }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.58rem' }}
                        >
                          Item {index + 1}
                        </Typography>
                        <LightTooltip
                          title={
                            <Box
                              sx={{
                                p: 0.75,
                                maxWidth: 360,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              <Typography variant="body1">
                                {item.productName ||
                                  item.category ||
                                  'Sin descripción'}
                              </Typography>
                            </Box>
                          }
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{
                              fontSize: '0.68rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.productName ||
                              item.category ||
                              'Sin descripción'}
                          </Typography>
                        </LightTooltip>
                        {item.buyerSpecification && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.58rem' }}
                          >
                            Especificación Comprador: {item.buyerSpecification}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sm={2}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.58rem' }}
                    >
                      Cantidad
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.68rem' }}>
                      {item.quantity ?? 'N/A'} {item.unit ?? ''}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    sm={3}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.58rem' }}
                    >
                      Total Neto
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.68rem' }}>
                      {item.netPrice ? formatMoney(item.netPrice) : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
};

export default POItemsSection;
